/**
 * A submission in progress survives a refresh.
 *
 * 3 Sep 2026 — during the AI-budget outage the AI check failed, and anyone who
 * refreshed or navigated away lost everything they had typed: CTC, expected,
 * notice, offer details, notes. Ten minutes of work, gone, and some recruiters
 * dropped the candidate rather than start again. The AI failing is a provider
 * problem; losing the form was ours.
 *
 * Deliberately local-only. This is a rescue for the tab that reloaded, not a
 * shared draft feature — no server round-trip, nothing to sync, nothing to
 * reconcile between two people editing the same candidate.
 */
const PREFIX = 'hj_submit_draft_v1:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_BYTES = 20000;

const key = (candidateId) => `${PREFIX}${candidateId}`;

export const saveSubmissionDraft = (candidateId, { job, form }) => {
  if (!candidateId || !form) return;
  try {
    const payload = JSON.stringify({
      savedAt: Date.now(),
      jobId: job?.id || null,
      jobTitle: job?.title || '',
      jobClient: job?.company_name || job?.client_name || '',
      form,
    });
    if (payload.length > MAX_BYTES) return;
    localStorage.setItem(key(candidateId), payload);
  } catch (_) { void _; }   // private mode / quota full: a draft is a nicety
};

export const readSubmissionDraft = (candidateId) => {
  if (!candidateId) return null;
  try {
    const raw = localStorage.getItem(key(candidateId));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.form || !draft?.savedAt || Date.now() - draft.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(key(candidateId));
      return null;
    }
    return draft;
  } catch (_) {
    try { localStorage.removeItem(key(candidateId)); } catch (__) { void __; }
    return null;
  }
};

export const clearSubmissionDraft = (candidateId) => {
  if (!candidateId) return;
  try { localStorage.removeItem(key(candidateId)); } catch (_) { void _; }
};

/** Logout: a draft holds CTC, offer details and notes — it must not outlive the session. */
export const clearAllSubmissionDrafts = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) { void _; }
};

export const draftAgeLabel = (savedAt) => {
  const mins = Math.round((Date.now() - savedAt) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};
